import { useState } from "react"
import { ChevronRight, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { CopyButton } from "./copy-button"

interface JsonViewerProps {
  data: unknown
  collapsed?: boolean
}

export function JsonViewer({ data, collapsed = false }: JsonViewerProps) {
  const jsonStr = JSON.stringify(data, null, 2)

  return (
    <div className="relative group rounded-lg glass-card overflow-hidden">
      <div className="absolute right-2 top-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <CopyButton text={jsonStr} />
      </div>
      <pre className="bg-muted/20 p-4 text-xs font-mono overflow-auto max-h-96">
        <JsonNode data={data} depth={0} defaultCollapsed={collapsed} />
      </pre>
    </div>
  )
}

function JsonNode({
  data,
  depth,
  defaultCollapsed,
}: {
  data: unknown
  depth: number
  defaultCollapsed: boolean
}) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed && depth > 0)

  if (data === null) return <span className="text-zinc-500">null</span>
  if (data === undefined) return <span className="text-zinc-500">undefined</span>
  if (typeof data === "boolean")
    return <span className="text-amber-600 dark:text-amber-400">{String(data)}</span>
  if (typeof data === "number")
    return <span className="text-blue-600 dark:text-blue-400">{data}</span>
  if (typeof data === "string")
    return <span className="text-emerald-600 dark:text-emerald-400">&quot;{data}&quot;</span>

  if (Array.isArray(data)) {
    if (data.length === 0) return <span className="text-zinc-500">{"[]"}</span>

    return (
      <span>
        <CollapsibleToggle isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
        {isCollapsed ? (
          <span className="text-zinc-500">[{data.length} items]</span>
        ) : (
          <>
            {"[\n"}
            {data.map((item, i) => (
              <span key={i}>
                <Indent depth={depth + 1} />
                <JsonNode data={item} depth={depth + 1} defaultCollapsed={defaultCollapsed} />
                {i < data.length - 1 ? ",\n" : "\n"}
              </span>
            ))}
            <Indent depth={depth} />{"]"}
          </>
        )}
      </span>
    )
  }

  if (typeof data === "object") {
    const entries = Object.entries(data as Record<string, unknown>)
    if (entries.length === 0) return <span className="text-zinc-500">{"{}"}</span>

    return (
      <span>
        <CollapsibleToggle isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />
        {isCollapsed ? (
          <span className="text-zinc-500">{`{${entries.length} keys}`}</span>
        ) : (
          <>
            {"{\n"}
            {entries.map(([key, value], i) => (
              <span key={key}>
                <Indent depth={depth + 1} />
                <span className="text-rose-600 dark:text-rose-400">&quot;{key}&quot;</span>
                <span className="text-zinc-500">: </span>
                <JsonNode data={value} depth={depth + 1} defaultCollapsed={defaultCollapsed} />
                {i < entries.length - 1 ? ",\n" : "\n"}
              </span>
            ))}
            <Indent depth={depth} />{"}"}
          </>
        )}
      </span>
    )
  }

  return <span>{String(data)}</span>
}

function Indent({ depth }: { depth: number }) {
  return <span>{"  ".repeat(depth)}</span>
}

function CollapsibleToggle({
  isCollapsed,
  onToggle,
}: {
  isCollapsed: boolean
  onToggle: () => void
}) {
  return (
    <button
      onClick={onToggle}
      className={cn(
        "inline-flex items-center justify-center w-4 h-4 -ml-4 mr-0",
        "text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300",
      )}
    >
      {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
    </button>
  )
}
